import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { useAuthStore } from '../../stores/authStore'
import { User, LogOut, ChevronDown, CheckCircle, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'

export const UserBadge: React.FC = () => {
  const navigate = useNavigate()
  const { user, isLoggingOut, logout } = useAuthStore()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  if (!user) return null

  // Use useCallback to ensure stable function references
  const handleLogout = useCallback(async () => {
    try {
      setIsDropdownOpen(false)
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }, [logout, navigate])

  const handleContinueSetup = useCallback(() => {
    setIsDropdownOpen(false)
    navigate('/onboarding')
  }, [navigate])

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      })
    }
  }, [isDropdownOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      
      // Check if click is outside both button and dropdown
      if (
        buttonRef.current && 
        !buttonRef.current.contains(target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(target)
      ) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      // Use capture phase to ensure we catch the event before other handlers
      document.addEventListener('mousedown', handleClickOutside, true)
      return () => document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [isDropdownOpen])

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isDropdownOpen])

  if (isLoggingOut) {
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 999999 }}>
        <div className="bg-white rounded-lg p-8 shadow-xl max-w-sm mx-4">
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-600 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Logging Out...
            </h3>
            <p className="text-gray-600 text-sm">
              Securely ending your StratIQX session
            </p>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  // Dropdown content component to ensure proper event handling
  const DropdownContent = () => (
    <div 
      ref={dropdownRef}
      className="fixed w-64 bg-white rounded-lg shadow-lg border border-gray-200"
      style={{ 
        top: dropdownPosition.top,
        right: dropdownPosition.right,
        zIndex: 999999
      }}
      onMouseDown={(e) => e.stopPropagation()} // Prevent event bubbling
    >
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-semibold">
            {getInitials(user.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {user.name}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {user.email}
            </div>
            <div className="flex items-center mt-1">
              <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-600 font-medium">
                Authenticated
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-2">
        <div className="px-3 py-2 text-xs text-gray-500 font-medium uppercase tracking-wider">
          Account
        </div>
        
        <button
          onClick={handleContinueSetup}
          className="w-full flex items-center justify-start text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-md transition-colors"
        >
          <User className="h-4 w-4 mr-3 text-gray-400" />
          Continue Setup
        </button>
        
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-start text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600 hover:text-red-700 rounded-md transition-colors"
        >
          <LogOut className="h-4 w-4 mr-3" />
          Sign Out
        </button>
      </div>

      <div className="p-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
        <p className="text-xs text-gray-500 text-center">
          StratIQX Intelligence Platform
        </p>
      </div>
    </div>
  )

  return (
    <>
      <Button
        ref={buttonRef}
        variant="ghost"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center gap-2 px-3 py-2 h-auto bg-white border border-gray-200 hover:bg-gray-50 shadow-sm relative"
      >
        <div className="flex items-center gap-2">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
            {getInitials(user.name)}
          </div>
          <div className="hidden sm:block text-left">
            <div className="text-sm font-medium text-gray-900 truncate max-w-32">
              {user.name}
            </div>
            <div className="text-xs text-gray-500 truncate max-w-32">
              {user.email}
            </div>
          </div>
          <div className="flex items-center text-gray-400">
            <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
            <ChevronDown className={cn(
              "h-4 w-4 transition-transform duration-200",
              isDropdownOpen && "transform rotate-180"
            )} />
          </div>
        </div>
      </Button>

      {/* Portal-based Dropdown Menu */}
      {isDropdownOpen && createPortal(
        <DropdownContent />,
        document.body
      )}
    </>
  )
}
